// Tooltip overlay — hand-rolled synchronous positioning.
//
// Floating UI was attempted earlier and removed: its computePosition is
// asynchronous, and the autoUpdate registration would fire its first callback
// during a different frame than the show toggle, which made the tooltip flash
// at the wrong coords / size before snapping into place. Doing the work
// synchronously inside show() — read anchor rect, force a layout to read
// tooltip size, compute, set translate, *then* show — eliminates the race.
//
// HTML contract:
//   <button data-bf-tooltip="Notifications" data-bf-tooltip-place="bottom">…</button>
//
// Optional attributes:
//   data-bf-tooltip          The text content (required when calling init()).
//   data-bf-tooltip-place    "top" | "right" | "bottom" | "left" (default "top").
//   data-bf-tooltip-delay    Show delay in ms (default 200).

import { $$, on, uid, clamp, type Cleanup } from './util';

type Side = 'top' | 'right' | 'bottom' | 'left';

interface Options {
  text?: string;
  placement?: Side;
  delay?: number;
  offset?: number;
}

const VIEWPORT_PADDING = 8;
const ARROW_HALF = 4;     // visible half-leg of the rotated arrow square
const ARROW_INSET = 8;    // minimum distance from a tooltip edge to the arrow center

let tip: HTMLDivElement | null = null;
let arrowEl: HTMLDivElement | null = null;
let activeAnchor: HTMLElement | null = null;
let showTimer: number | undefined;

function ensureTip(): { tip: HTMLDivElement; arrow: HTMLDivElement } {
  if (tip && arrowEl) return { tip, arrow: arrowEl };
  tip = document.createElement('div');
  tip.className = 'bf-tooltip';
  tip.role = 'tooltip';
  arrowEl = document.createElement('div');
  arrowEl.className = 'bf-tooltip__arrow';
  tip.appendChild(arrowEl);
  document.body.appendChild(tip);
  return { tip, arrow: arrowEl };
}

function setText(text: string): void {
  const { tip } = ensureTip();
  for (let n = tip.firstChild; n; ) {
    const next = n.nextSibling;
    if (n !== arrowEl) tip.removeChild(n);
    n = next;
  }
  tip.insertBefore(document.createTextNode(text), arrowEl);
}

// Fully synchronous positioning. Reads the anchor and tooltip rects in the
// current frame and writes the final translate; no promises, no autoUpdate.
function position(anchor: HTMLElement, requested: Side, gap: number): void {
  const { tip, arrow: ar } = ensureTip();

  const a = anchor.getBoundingClientRect();
  // Reading offsetWidth/Height forces sync layout, which is exactly what we
  // want — the tooltip's text was just changed, and we need the post-layout
  // dimensions before deciding where to put it.
  const tw = tip.offsetWidth;
  const th = tip.offsetHeight;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Flip if the requested side doesn't fit; only flips to its opposite.
  let side: Side = requested;
  const fits = (s: Side): boolean => {
    switch (s) {
      case 'top':    return a.top    >= th + gap + VIEWPORT_PADDING;
      case 'bottom': return vh - a.bottom >= th + gap + VIEWPORT_PADDING;
      case 'left':   return a.left   >= tw + gap + VIEWPORT_PADDING;
      case 'right':  return vw - a.right >= tw + gap + VIEWPORT_PADDING;
    }
  };
  const opposite = ({ top: 'bottom', bottom: 'top', left: 'right', right: 'left' } as const)[requested];
  if (!fits(side) && fits(opposite)) side = opposite;

  let x = 0, y = 0;
  switch (side) {
    case 'top':    x = a.left + a.width / 2 - tw / 2; y = a.top - th - gap; break;
    case 'bottom': x = a.left + a.width / 2 - tw / 2; y = a.bottom + gap;    break;
    case 'left':   x = a.left - tw - gap;             y = a.top + a.height / 2 - th / 2; break;
    case 'right':  x = a.right + gap;                 y = a.top + a.height / 2 - th / 2; break;
  }

  // Shift along the cross axis to keep the tooltip in the viewport.
  if (side === 'top' || side === 'bottom') {
    x = clamp(x, VIEWPORT_PADDING, vw - tw - VIEWPORT_PADDING);
  } else {
    y = clamp(y, VIEWPORT_PADDING, vh - th - VIEWPORT_PADDING);
  }

  // Arrow: pin to the anchor center, but stay at least ARROW_INSET from
  // either tooltip edge so it doesn't poke past the corner radius.
  if (side === 'top' || side === 'bottom') {
    const cx = a.left + a.width / 2;
    const ax = clamp(cx - x - ARROW_HALF, ARROW_INSET, tw - ARROW_INSET - ARROW_HALF * 2);
    ar.style.left = `${ax}px`;
    ar.style.top = '';
    ar.style[side === 'top' ? 'bottom' : 'top'] = `-${ARROW_HALF}px`;
    ar.style[side === 'top' ? 'top' : 'bottom'] = '';
  } else {
    const cy = a.top + a.height / 2;
    const ay = clamp(cy - y - ARROW_HALF, ARROW_INSET, th - ARROW_INSET - ARROW_HALF * 2);
    ar.style.top = `${ay}px`;
    ar.style.left = '';
    ar.style[side === 'left' ? 'right' : 'left'] = `-${ARROW_HALF}px`;
    ar.style[side === 'left' ? 'left' : 'right'] = '';
  }

  tip.style.translate = `${Math.round(x)}px ${Math.round(y)}px`;
  tip.style.setProperty('--bf-tooltip-side', side);
}

function show(anchor: HTMLElement, opts: Options): void {
  const text = opts.text ?? anchor.getAttribute('data-bf-tooltip') ?? anchor.getAttribute('aria-label');
  if (!text) return;
  const placement: Side = opts.placement
    ?? (anchor.getAttribute('data-bf-tooltip-place') as Side | null)
    ?? 'top';
  const gap = opts.offset ?? 6;

  const { tip } = ensureTip();

  // The hard part is re-showing while a previous fade-out (or even a previous
  // fade-in for a different anchor) is still in flight. If we just change the
  // text and translate now, the in-flight opacity transition would happily
  // animate the new content from "wherever it currently is" to its target —
  // visibly drifting. The cure is to temporarily disable transitions, snap
  // every property to its final pre-show state, force layout to commit it,
  // then re-enable transitions and trigger the entrance. All sync — no rAF,
  // no promises.

  // Step 1: kill any in-flight transition and reset the show flag.
  tip.style.transition = 'none';
  tip.dataset.bfShow = 'false';

  // Step 2: stage the new content. setText also clears any stale text that
  // would otherwise be measured by position()'s offsetWidth read.
  setText(text);

  if (!anchor.id) anchor.id = uid('bf-tt');
  anchor.setAttribute('aria-describedby', anchor.id);

  // Step 3: compute and write the final position synchronously. The browser
  // hasn't painted yet — we're inside one task.
  position(anchor, placement, gap);

  // Step 4: force a layout flush so the "transition:none + opacity:0 + final
  // translate" state is what the browser sees as the entrance baseline.
  // Without this read the browser may coalesce the next set of style writes
  // into a single transition, defeating the cancellation.
  void tip.offsetWidth;

  // Step 5: re-enable transitions (drops the inline override; the @layer
  // rule's opacity transition becomes active again) and flip the show flag.
  tip.style.transition = '';
  tip.dataset.bfShow = 'true';

  activeAnchor = anchor;
}

function hide(): void {
  if (tip) tip.dataset.bfShow = 'false';
  if (activeAnchor) activeAnchor.removeAttribute('aria-describedby');
  activeAnchor = null;
}

export function attach(anchor: HTMLElement, opts: Options = {}): Cleanup {
  const delayAttr = anchor.getAttribute('data-bf-tooltip-delay');
  const delay = opts.delay ?? (delayAttr ? Number(delayAttr) : 200);
  const handlers: Cleanup[] = [];

  handlers.push(on(anchor, 'mouseenter', () => {
    window.clearTimeout(showTimer);
    showTimer = window.setTimeout(() => show(anchor, opts), delay);
  }));
  handlers.push(on(anchor, 'mouseleave', () => {
    window.clearTimeout(showTimer);
    hide();
  }));
  handlers.push(on(anchor, 'focus', () => show(anchor, opts)));
  handlers.push(on(anchor, 'blur', () => hide()));
  return () => handlers.forEach(c => c());
}

export function init(root: ParentNode = document): void {
  for (const el of $$<HTMLElement>('[data-bf-tooltip]', root)) attach(el);
}
